import { useEffect, useRef, useState } from 'react';
import type { CompositionEdge, CompositionNode, DiagramNode, Domain, GraphEdge, GraphNode } from '@civil/schema';
import { diagramShown, type Altitude } from '../canvas/Editor.js';
import type { LiftUnit, ProjectBundle } from '../project.js';
import { confidenceSentence, parseEvidence, voicesSentence } from '../lift.js';
import { NodeCrossings, PartCrossings, type GoTo } from './Crossings.js';

/**
 * PRD 5: agents and io nodes are leaves — no descent, everything inspector-editable.
 * This is where "inspector-editable" becomes true: each field commits an updateNode
 * patch, the id commits a renameNode, and the buttons at the bottom are the same ops
 * the Delete key sends. Nothing here writes YAML; it posts ops like everything else
 * (PRD 7.1).
 */
export function Inspector({
  bundle,
  altitude,
  selectedIds,
  selectedEdgeIds,
  onPatch,
  onPatchEdge,
  onRename,
  onRemoveNode,
  onRemoveEdge,
  onSavePrompt,
  onOpenFile,
  onGoTo,
}: {
  bundle: ProjectBundle | undefined;
  altitude: Altitude;
  selectedIds: string[];
  selectedEdgeIds: string[];
  onPatch: (id: string, patch: Record<string, unknown>) => void;
  onPatchEdge: (id: string, patch: Record<string, unknown>) => void;
  onRename: (from: string, to: string) => void;
  onRemoveNode: (id: string) => void;
  onRemoveEdge: (id: string) => void;
  onSavePrompt: (path: string, content: string) => Promise<boolean>;
  /** Opens a repository file in the code view, revealing `line` when given. */
  onOpenFile?: (path: string, line?: number) => void;
  /** Goes to a node of Flow or Architecture, selected (prd-deltas §25). */
  onGoTo?: GoTo;
}) {
  // Detail is for a selection of one; a box-select gets a count and the Delete
  // hint, because bulk-editing fields across nodes is not a thing that means
  // anything.
  const single = selectedIds.length === 1 && selectedEdgeIds.length === 0;
  const singleEdge = selectedEdgeIds.length === 1 && selectedIds.length === 0;
  const many = selectedIds.length + selectedEdgeIds.length > 1;
  // A flow or architecture view's node is the diagram's, never the composition's —
  // even when an id is shared — so it is looked up there first.
  const shown = bundle ? diagramShown(bundle, altitude) : undefined;
  const role = single && shown ? shown.doc.spec.nodes.find((n) => n.id === selectedIds[0]) : undefined;
  const node = single && !shown ? findNode(bundle, altitude, selectedIds[0]!) : undefined;
  // A domain is the view's, not the composition's (§21): its own detail, from its file.
  const loaded = altitude.kind === 'composition' && altitude.view ? bundle?.views?.[altitude.view] : undefined;
  const domain = single && !node ? loaded?.domains[selectedIds[0]!] : undefined;
  const edge = singleEdge && !shown ? findEdge(bundle, altitude, selectedEdgeIds[0]!) : undefined;

  return (
    <>
      <div className="pane-title">Inspector</div>
      <div className="pane-body">
        {role && shown ? (
          <>
            <RoleDetail node={role} path={shown.path} {...(onOpenFile ? { onOpenFile } : {})} />
            {bundle && onGoTo && altitude.kind === 'composition' && altitude.view ? (
              <NodeCrossings
                bundle={bundle}
                view={altitude.view}
                node={role}
                onGoTo={onGoTo}
                {...(onOpenFile ? { onOpenFile } : {})}
              />
            ) : null}
          </>
        ) : domain ? (
          <DomainDetail
            id={selectedIds[0]!}
            path={domain.path}
            doc={domain.doc}
            {...(onOpenFile ? { onOpenFile } : {})}
          />
        ) : node ? (
          <>
            <NodeDetail
              node={node}
              bundle={bundle}
              graphPath={altitude.kind === 'graph' ? altitude.path : undefined}
              onPatch={(patch) => onPatch(node.id, patch)}
              onRename={(to) => onRename(node.id, to)}
              onRemove={() => onRemoveNode(node.id)}
              onSavePrompt={onSavePrompt}
              {...(onOpenFile ? { onOpenFile } : {})}
            />
            {bundle && onGoTo && altitude.kind === 'composition' ? (
              <PartCrossings bundle={bundle} part={node.id} onGoTo={onGoTo} />
            ) : null}
          </>
        ) : edge ? (
          <EdgeDetail
            edge={edge}
            nodes={nodesOf(bundle, altitude)}
            onPatch={(patch) => onPatchEdge(edge.id, patch)}
            onRemove={() => onRemoveEdge(edge.id)}
          />
        ) : many ? (
          <p className="muted">
            {selectedIds.length > 0
              ? `${selectedIds.length} node${selectedIds.length === 1 ? '' : 's'}`
              : ''}
            {selectedIds.length > 0 && selectedEdgeIds.length > 0 ? ' and ' : ''}
            {selectedEdgeIds.length > 0
              ? `${selectedEdgeIds.length} edge${selectedEdgeIds.length === 1 ? '' : 's'}`
              : ''}{' '}
            selected. Delete removes them together.
          </p>
        ) : (
          <p className="muted">Nothing selected.</p>
        )}

        {bundle && bundle.diagnostics.length > 0 ? (
          <>
            <h3 className="section">Diagnostics</h3>
            {bundle.diagnostics.map((d, i) => (
              <div key={i} className={`diagnostic diagnostic-${d.severity}`}>
                <div className="diagnostic-code">{d.code}</div>
                <div>{d.message}</div>
                <div className="diagnostic-where">
                  {d.file}
                  {d.jsonPointer}
                </div>
              </div>
            ))}
          </>
        ) : null}
      </div>
    </>
  );
}

/**
 * One editable value. Commits on Enter or blur, only when it changed; Escape puts
 * the draft back and goes no further — reverting a field must not also clear the
 * canvas selection, which is what the global Escape would do.
 */
/**
 * The agent's objective, editable in place. Same contract as Field — commit on
 * blur (or Cmd/Ctrl+Enter), snap back when the save is refused — sized for prose.
 * An absent prompt file starts empty and the first save creates it.
 */
function PromptEditor({
  value,
  onSave,
}: {
  value: string | undefined;
  onSave: (content: string) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const sent = useRef<string | null>(null);
  useEffect(() => {
    setDraft(value ?? '');
    sent.current = null;
  }, [value]);

  const commit = () => {
    if (draft === (value ?? '') || sent.current === draft) return;
    sent.current = draft;
    setSaving(true);
    void onSave(draft)
      .then((ok) => {
        if (!ok) {
          setDraft(value ?? '');
          sent.current = null;
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <textarea
        className="prompt prompt-edit"
        value={draft}
        placeholder="What should this agent do? Saved to the prompt file."
        spellCheck={false}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') e.currentTarget.blur();
        }}
      />
      {saving ? <p className="muted">saving…</p> : null}
    </>
  );
}

function Field({
  label,
  value,
  placeholder,
  onCommit,
}: {
  label: string;
  value: string;
  placeholder?: string;
  /** Returning false means the value was not accepted; the draft snaps back. */
  onCommit: (next: string) => void | false;
}) {
  const [draft, setDraft] = useState(value);
  // What was last sent, so Enter-then-blur is one commit, not two ops.
  const sent = useRef<string | null>(null);
  // A new node under the cursor replaces the draft; typing does not.
  useEffect(() => {
    setDraft(value);
    sent.current = null;
  }, [value]);

  const commit = () => {
    const next = draft.trim();
    if (next === value || sent.current === next) return;
    sent.current = next;
    if (onCommit(next) === false) {
      setDraft(value);
      sent.current = null;
    }
  };

  return (
    <div style={{ display: 'contents' }}>
      <dt>{label}</dt>
      <dd>
        <input
          className="field"
          value={draft}
          placeholder={placeholder ?? ''}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === 'Escape') {
              e.stopPropagation();
              setDraft(value);
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      </dd>
    </div>
  );
}

/** A comma-separated list over an array field. Empty means an empty list. */
const parseList = (text: string): string[] =>
  text
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

/** Enough of a lifted node's files to see what it is; the registry has the rest. */
const REPO_FILES_SHOWN = 12;

/** The directory every path shares ("apps/server/src/modules/tasks/"), so the list reads short. */
function commonDir(paths: readonly string[]): string {
  if (paths.length < 2) return '';
  let prefix = paths[0]!.slice(0, paths[0]!.lastIndexOf('/') + 1);
  for (const p of paths) while (prefix && !p.startsWith(prefix)) prefix = prefix.slice(0, prefix.slice(0, -1).lastIndexOf('/') + 1);
  return prefix;
}

/** The same marks the canvas uses: quiet when sure, a question when not. */
const CONFIDENCE_GLYPH = { high: '✓', medium: '~', low: '?' } as const;

/** Enough cited places to see why; the registry has them all. */
const EVIDENCE_SHOWN = 8;

/**
 * How sure Civil is that this node is there, and why (docs/lift-repo.md, "Claims"):
 * the weighed confidence in a sentence, the voices that support it, and the places in
 * the code it rests on — each opens the file at its line. Nothing when the lift
 * recorded no provenance (a reader-only lift, or an older server).
 */
function Provenance({
  unit,
  onOpenFile,
}: {
  unit: LiftUnit;
  onOpenFile: ((path: string, line?: number) => void) | undefined;
}) {
  const voices = voicesSentence(unit.voices);
  // Said relative to the directory the unit's files share, like the file list below;
  // the full path is the title.
  const dir = commonDir(unit.files);
  const short = (item: string) => (dir && item.startsWith(dir) ? item.slice(dir.length) : item);
  const evidence = (unit.evidence ?? [])
    .map((item) => ({ item, parsed: parseEvidence(item) }))
    .filter((e): e is { item: string; parsed: { path: string; line?: number } } => e.parsed !== null);
  if (!unit.confidence && !voices && evidence.length === 0) return null;

  return (
    <div className="provenance">
      {unit.confidence ? (
        <p className="provenance-confidence">
          <span className={`confidence-badge is-${unit.confidence}`} aria-hidden="true">
            {CONFIDENCE_GLYPH[unit.confidence]}
          </span>
          <span>{confidenceSentence(unit.confidence, unit.voices)}.</span>
        </p>
      ) : null}
      {voices ? (
        <p className="muted provenance-voices" title={unit.voices?.join(', ')}>
          Supported by {voices}.
        </p>
      ) : null}
      {evidence.length > 0 ? (
        <>
          <div className="provenance-label muted">Where it rests in the code</div>
          <ul className="repo-files provenance-evidence">
            {evidence.slice(0, EVIDENCE_SHOWN).map(({ item, parsed }, i) => (
              <li key={`${i}-${item}`} title={parsed.line ? `Open ${parsed.path} at line ${parsed.line}` : `Open ${parsed.path}`}>
                {onOpenFile ? (
                  <button
                    type="button"
                    className="repo-file-link"
                    onClick={() => onOpenFile(parsed.path, parsed.line)}
                  >
                    <code>{short(item)}</code>
                  </button>
                ) : (
                  <code>{short(item)}</code>
                )}
              </li>
            ))}
            {evidence.length > EVIDENCE_SHOWN ? (
              <li className="muted">and {evidence.length - EVIDENCE_SHOWN} more in civil/registry.yaml</li>
            ) : null}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function NodeDetail({
  node,
  bundle,
  graphPath,
  onPatch,
  onRename,
  onRemove,
  onSavePrompt,
  onOpenFile,
}: {
  node: CompositionNode | GraphNode;
  bundle: ProjectBundle | undefined;
  /** The graph the node lives in, so its agent entry resolves by graphPath#nodeId. */
  graphPath: string | undefined;
  onPatch: (patch: Record<string, unknown>) => void;
  onRename: (to: string) => void;
  onRemove: () => void;
  onSavePrompt: (path: string, content: string) => Promise<boolean>;
  onOpenFile?: (path: string, line?: number) => void;
}) {
  // An optional string field: emptying it removes the key rather than writing "".
  const optional = (key: string) => (v: string) => onPatch({ [key]: v === '' ? null : v });
  // A required field emptied is not a change the manifest can take; the field snaps
  // back to the value it still has rather than sitting blank over an unchanged file.
  const required = (key: string) => (v: string): void | false => {
    if (v === '') return false;
    onPatch({ [key]: v });
  };

  const fields: React.ReactNode[] = [];
  switch (node.type) {
    case 'client':
      fields.push(
        <Field key="path" label="path" value={node.path} onCommit={required('path')} />,
        <Field key="dev" label="dev" value={node.dev ?? ''} placeholder="dev command" onCommit={optional('dev')} />,
      );
      break;
    case 'boundary':
      // Wiring boundaries explicitly means the canvas shows what is reachable
      // from outside. A service with no boundary in front of it is not exposed.
      fields.push(
        <Field
          key="exposes"
          label="exposes"
          value={node.exposes.join(', ')}
          placeholder="service ids, comma-separated"
          onCommit={(v) => onPatch({ exposes: parseList(v) })}
        />,
      );
      break;
    case 'service': {
      const kind = 'graph' in node.impl ? 'graph' : 'entrypoint';
      const value = 'graph' in node.impl ? node.impl.graph : node.impl.entrypoint;
      fields.push(
        <Field
          key="impl"
          label={kind}
          value={value}
          onCommit={(v) => {
            if (v === '') return false;
            onPatch({ impl: { [kind]: v } });
          }}
        />,
      );
      break;
    }
    case 'process':
      fields.push(
        <Field
          key="cron"
          label="cron"
          value={node.trigger.cron}
          onCommit={(v) => {
            if (v === '') return false;
            onPatch({ trigger: { kind: 'schedule', cron: v } });
          }}
        />,
        <Field
          key="calls"
          label="calls"
          value={node.calls.join(', ')}
          placeholder="service ids, comma-separated"
          onCommit={(v) => onPatch({ calls: parseList(v) })}
        />,
      );
      break;
    case 'datastore':
      // Descriptive, not a driver choice ("postgres", "redis", "gcs"); emptying it
      // removes the key.
      fields.push(
        <Field
          key="engine"
          label="engine"
          value={node.engine ?? ''}
          placeholder="postgres, redis, gcs…"
          onCommit={optional('engine')}
        />,
      );
      break;
    case 'io':
      fields.push(
        <div key="direction" style={{ display: 'contents' }}>
          <dt>direction</dt>
          <dd>
            <select
              className="field"
              value={node.direction}
              onChange={(e) => onPatch({ direction: e.target.value })}
            >
              <option value="in">in</option>
              <option value="out">out</option>
            </select>
          </dd>
        </div>,
        <Field key="name" label="name" value={node.name ?? ''} onCommit={optional('name')} />,
        <Field key="schema" label="schema" value={node.schema ?? ''} placeholder="schemas/….json" onCommit={optional('schema')} />,
        <div key="kind" style={{ display: 'contents' }}>
          <dt>progress</dt>
          <dd>
            <select
              className="field"
              value={node.kind ?? ''}
              onChange={(e) => onPatch({ kind: e.target.value === '' ? null : 'progress' })}
            >
              <option value="">no</option>
              <option value="progress">yes</option>
            </select>
          </dd>
        </div>,
      );
      break;
    case 'code':
      fields.push(
        <Field key="name" label="name" value={node.name ?? ''} onCommit={optional('name')} />,
        <Field
          key="include"
          label="include"
          value={node.include.join(', ')}
          placeholder="globs, comma-separated"
          onCommit={(v) => onPatch({ include: parseList(v) })}
        />,
        <Field
          key="entrypoint"
          label="entrypoint"
          value={node.entrypoint ?? ''}
          placeholder="empty = capability target only"
          onCommit={optional('entrypoint')}
        />,
      );
      break;
    case 'subgraph':
      fields.push(<Field key="ref" label="ref" value={node.ref} onCommit={required('ref')} />);
      break;
    case 'agent':
      // agent.yaml has dissolved (docs/emitted-code.md): no ref. The node carries
      // only identity, wiring, and an optional display name.
      fields.push(
        <Field key="name" label="name" value={node.name ?? ''} onCommit={optional('name')} />,
      );
      break;
  }

  const agent =
    node.type === 'agent' && graphPath ? bundle?.agents[`${graphPath}#${node.id}`] : undefined;
  // A composition node a lift wrote: what it stands for in the repository — the
  // model's sentence on it and the files the registry lists — which otherwise lives
  // only in civil/architecture.md (docs/lift-repo.md).
  const fromRepo = graphPath === undefined ? bundle?.lift?.units[node.id] : undefined;

  return (
    <>
      <dl className="kv">
        {/* The id commits a rename, which rewrites every reference to it (PRD 7.1). */}
        <Field label="id" value={node.id} onCommit={onRename} />
        <dt>type</dt>
        <dd>
          {node.type === 'client'
            ? `client · ${node.client}`
            : node.type === 'boundary'
              ? `boundary · ${node.boundary}`
              : node.type}
        </dd>
        {fields}
      </dl>

      {fromRepo ? (
        <>
          <h3 className="section">From the repo</h3>
          {fromRepo.description ? <p className="repo-description">{fromRepo.description}</p> : null}
          <Provenance unit={fromRepo} onOpenFile={onOpenFile} />
          <p className="muted">
            {fromRepo.files.length} file{fromRepo.files.length === 1 ? '' : 's'} implement it (civil/registry.yaml); its
            routes are listed in civil/architecture.md.
          </p>
          <ul className="repo-files">
            {fromRepo.files.slice(0, REPO_FILES_SHOWN).map((file) => (
              <li key={file} title={file}>
                {onOpenFile ? (
                  <button type="button" className="repo-file-link" onClick={() => onOpenFile(file)}>
                    <code>{file.slice(commonDir(fromRepo.files).length)}</code>
                  </button>
                ) : (
                  <code>{file.slice(commonDir(fromRepo.files).length)}</code>
                )}
              </li>
            ))}
            {fromRepo.files.length > REPO_FILES_SHOWN ? (
              <li className="muted">and {fromRepo.files.length - REPO_FILES_SHOWN} more</li>
            ) : null}
          </ul>
        </>
      ) : null}

      {agent ? (
        <>
          <h3 className="section">Agent</h3>
          {/* agent.yaml has dissolved (docs/emitted-code.md): model and turn budget
              are literal kwargs in the emitted agent module — edit them in code, the
              established rule for statically-liftable config. */}
          <p className="muted">Model and turn budget are literal kwargs in the emitted agent code.</p>
          <h3 className="section">Objective</h3>
          {/* The prompt is a file at prompts/<node-id>.md, and this edits that file —
              the inspector's rule for agents: the objective maps to a real repo file.
              Keyed by the prompt path so switching agents replaces the draft. */}
          <PromptEditor
            key={agent.promptPath}
            value={agent.prompt}
            onSave={(content) => onSavePrompt(agent.promptPath, content)}
          />
        </>
      ) : null}

      <button type="button" className="danger-link" onClick={onRemove}>
        Remove node
      </button>
    </>
  );
}

function EdgeDetail({
  edge,
  nodes,
  onPatch,
  onRemove,
}: {
  edge: CompositionEdge | GraphEdge;
  nodes: readonly (CompositionNode | GraphNode)[];
  onPatch: (patch: Record<string, unknown>) => void;
  onRemove: () => void;
}) {
  const from = nodes.find((n) => n.id === edge.from.node);
  const to = nodes.find((n) => n.id === edge.to.node);

  /**
   * Almost every edge's kind is implied by what it connects — client → boundary
   * can only route, io → code can only flow — so offering a control there would
   * be a question with one answer. The single genuine choice is agent → code:
   * a tool grant (capability) or the agent's output feeding a step (flow).
   */
  const kindIsAChoice = from?.type === 'agent' && to?.type === 'code';
  const capability = edge.kind === 'capability';

  return (
    <>
      <dl className="kv">
        <dt>edge</dt>
        <dd>{edge.id}</dd>
        <dt>kind</dt>
        {kindIsAChoice ? (
          <dd>
            <select
              className="field"
              value={edge.kind}
              onChange={(e) => onPatch({ kind: e.target.value })}
            >
              <option value="capability">capability — tools the agent may call</option>
              <option value="flow">flow — output feeds the step</option>
            </select>
          </dd>
        ) : (
          <dd title="Implied by what this edge connects. To change it, change the endpoints.">
            {edge.kind}
          </dd>
        )}
        <dt>from</dt>
        <dd>{edge.from.node}</dd>
        <dt>to</dt>
        <dd>{edge.to.node}</dd>
        {capability ? (
          <Field
            label="function"
            value={'function' in edge.to ? (edge.to.function ?? '') : ''}
            placeholder="any public function"
            onCommit={(v) =>
              onPatch({ to: v === '' ? { node: edge.to.node } : { node: edge.to.node, function: v } })
            }
          />
        ) : null}
      </dl>
      <button type="button" className="danger-link" onClick={onRemove}>
        Disconnect
      </button>
    </>
  );
}

/**
 * A domain of a view: what it is, what it holds, and where its reading rests. Read
 * here; arranged in its file (civil/views/<view>/<id>.yaml), which the link opens.
 */
function DomainDetail({
  id,
  path,
  doc,
  onOpenFile,
}: {
  id: string;
  path: string;
  doc: Domain;
  onOpenFile?: (path: string, line?: number) => void;
}) {
  const link = (target: string) => {
    const [file, line] = target.split(':');
    return onOpenFile ? (
      <button type="button" className="repo-file-link" onClick={() => onOpenFile(file!, line ? Number(line) : undefined)}>
        <code>{target}</code>
      </button>
    ) : (
      <code>{target}</code>
    );
  };
  return (
    <>
      <dl className="kv">
        <dt>domain</dt>
        <dd>{doc.metadata.name ?? id}{doc.spec.foundation ? ' · foundation' : ''}</dd>
        <dt>holds</dt>
        <dd>{doc.spec.members.length ? doc.spec.members.join(', ') : 'only domains'}</dd>
        {doc.spec.unsure?.length ? (
          <>
            <dt>unsure</dt>
            <dd title="Placed here on weak evidence.">{doc.spec.unsure.join(', ')}</dd>
          </>
        ) : null}
        <dt>file</dt>
        <dd>{link(path)}</dd>
      </dl>
      {doc.spec.description ? <p className="repo-description">{doc.spec.description}</p> : null}
      {doc.spec.evidence?.length ? (
        <>
          <div className="provenance-label muted">Where it rests in the code</div>
          <ul className="repo-files provenance-evidence">
            {doc.spec.evidence.map((e, i) => (
              <li key={`${i}-${e}`}>{link(e)}</li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}

/**
 * A node of a flow or architecture diagram (docs/interpretation.md): what it is, the
 * decisions that define it and why, where that rests in the code and docs, and the
 * parts of the composition that implement it.
 */
function RoleDetail({
  node,
  path,
  onOpenFile,
}: {
  node: DiagramNode;
  path: string;
  onOpenFile?: (path: string, line?: number) => void;
}) {
  const link = (target: string, i: number) => {
    const [file, line] = target.split(':');
    return (
      <li key={`${i}-${target}`}>
        {onOpenFile ? (
          <button type="button" className="repo-file-link" onClick={() => onOpenFile(file!, line ? Number(line) : undefined)}>
            <code>{target}</code>
          </button>
        ) : (
          <code>{target}</code>
        )}
      </li>
    );
  };
  return (
    <>
      <dl className="kv">
        <dt>{node.kind}</dt>
        <dd>{node.name}</dd>
        {node.realizedBy.length ? (
          <>
            <dt>parts</dt>
            <dd>{node.realizedBy.join(', ')}</dd>
          </>
        ) : null}
        {node.confidence ? (
          <>
            <dt>confidence</dt>
            <dd>{node.confidence}</dd>
          </>
        ) : null}
        <dt>file</dt>
        <dd>
          <ul className="repo-files">{link(path, 0)}</ul>
        </dd>
      </dl>
      {node.summary ? <p className="repo-description">{node.summary}</p> : null}
      {node.decisions.length ? (
        <>
          <div className="provenance-label muted">Decisions</div>
          {node.decisions.map((d, i) => (
            <div key={i} className="decision">
              <p>
                {d.statement}
                {d.source ? <span className="muted"> · {d.source}</span> : null}
              </p>
              {d.why ? <p className="muted">Why: {d.why}</p> : null}
              {d.evidence.length ? <ul className="repo-files provenance-evidence">{d.evidence.map(link)}</ul> : null}
            </div>
          ))}
        </>
      ) : null}
      {node.evidence.length ? (
        <>
          <div className="provenance-label muted">Where it rests</div>
          <ul className="repo-files provenance-evidence">{node.evidence.map(link)}</ul>
        </>
      ) : null}
    </>
  );
}

function nodesOf(
  bundle: ProjectBundle | undefined,
  altitude: Altitude,
): readonly (CompositionNode | GraphNode)[] {
  if (!bundle || altitude.kind === 'code') return [];
  if (altitude.kind === 'composition') return bundle.composition?.spec.nodes ?? [];
  return bundle.graphs[altitude.path]?.spec.nodes ?? [];
}

function findNode(
  bundle: ProjectBundle | undefined,
  altitude: Altitude,
  selectedId: string | null,
): CompositionNode | GraphNode | undefined {
  if (!bundle || !selectedId) return undefined;
  if (altitude.kind === 'composition') {
    return bundle.composition?.spec.nodes.find((n) => n.id === selectedId);
  }
  // A code takeover has no node selection of its own; the inspector shows the
  // diagnostics list until you ascend.
  if (altitude.kind === 'code') return undefined;
  return bundle.graphs[altitude.path]?.spec.nodes.find((n) => n.id === selectedId);
}

function findEdge(
  bundle: ProjectBundle | undefined,
  altitude: Altitude,
  selectedEdgeId: string | null,
): CompositionEdge | GraphEdge | undefined {
  if (!bundle || !selectedEdgeId) return undefined;
  if (altitude.kind === 'composition') {
    return bundle.composition?.spec.edges.find((e) => e.id === selectedEdgeId);
  }
  if (altitude.kind === 'code') return undefined;
  return bundle.graphs[altitude.path]?.spec.edges.find((e) => e.id === selectedEdgeId);
}
